import { useReducer, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { OnboardingState, OnboardingAction } from '@/types/onboarding'
import { ONBOARDING_STEP_COUNT } from '@/data/onboarding-steps'
import { OnboardingContext, type ForwardActionConfig } from './OnboardingContext'
import { useUpdateOnboardingProgress } from '@/hooks/useOnboardingProgress'

function onboardingReducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.step }
    case 'SET_STEP_DATA':
      return {
        ...state,
        stepData: { ...state.stepData, [action.step]: action.data },
      }
    case 'MARK_COMPLETED':
      return {
        ...state,
        completedSteps: new Set([...state.completedSteps, action.step]),
      }
    default:
      return state
  }
}

interface OnboardingProviderProps {
  initialStep?: number
  initialCompletedSteps?: number[]
  initialStepData?: Record<string, unknown>
  children: ReactNode
}

function clampStep(step: number) {
  return Math.max(0, Math.min(step, ONBOARDING_STEP_COUNT - 1))
}

export function OnboardingProvider({
  initialStep = 0,
  initialCompletedSteps = [],
  initialStepData = {},
  children,
}: OnboardingProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams()

  const [state, dispatch] = useReducer(
    onboardingReducer,
    undefined,
    (): OnboardingState => {
      const stepParam = searchParams.get('step')
      const parsed = stepParam !== null ? parseInt(stepParam, 10) : initialStep
      return {
        currentStep: clampStep(isNaN(parsed) ? initialStep : parsed),
        completedSteps: new Set(initialCompletedSteps),
        stepData: initialStepData,
      }
    },
  )

  // stateRef mirrors the reducer synchronously so multiple dispatches in one
  // event tick (e.g. markCompleted + goToStep) see each other's updates when
  // computing what to persist. Without this, each callback reads closure-
  // captured state from the prior render and the second PUT would overwrite
  // the first with stale completedSteps/currentStep.
  const stateRef = useRef(state)

  const [forwardAction, setForwardAction] = useState<ForwardActionConfig | null>(null)
  const { mutate: persistProgress } = useUpdateOnboardingProgress()

  useEffect(() => {
    if (searchParams.get('step') !== String(state.currentStep)) {
      setSearchParams(
        (prev) => {
          prev.set('step', String(state.currentStep))
          return prev
        },
        { replace: true },
      )
    }
  }, [state.currentStep, searchParams, setSearchParams])

  const dispatchAndPersist = useCallback(
    (action: OnboardingAction) => {
      const next = onboardingReducer(stateRef.current, action)
      stateRef.current = next
      dispatch(action)
      persistProgress({
        current_step: next.currentStep,
        completed_steps: [...next.completedSteps],
        step_data: next.stepData,
      })
    },
    [persistProgress],
  )

  const goToStep = useCallback(
    (step: number) => {
      if (step < 0 || step >= ONBOARDING_STEP_COUNT) return
      dispatchAndPersist({ type: 'SET_STEP', step })
    },
    [dispatchAndPersist],
  )

  const setStepData = useCallback(
    (step: number, data: unknown) => {
      dispatchAndPersist({ type: 'SET_STEP_DATA', step, data })
    },
    [dispatchAndPersist],
  )

  const markCompleted = useCallback(
    (step: number) => {
      dispatchAndPersist({ type: 'MARK_COMPLETED', step })
    },
    [dispatchAndPersist],
  )

  const contextValue = useMemo(
    () => ({
      state,
      goToStep,
      setStepData,
      markCompleted,
      forwardAction,
      setForwardAction,
    }),
    [state, goToStep, setStepData, markCompleted, forwardAction],
  )

  return <OnboardingContext.Provider value={contextValue}>{children}</OnboardingContext.Provider>
}
